import type { Theme } from '@mui/material/styles';
import type { PerformanceBand } from 'switch-fe-shared/components/Charts/recharts/types';

/** Ratios of actual to forecast bounding the poor, fair and good performance bands. */
export const SOLAR_PERFORMANCE_THRESHOLDS = {
  poor: 0.7,
  good: 0.9,
  ceiling: 1.15,
} as const;

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Series colours of the solar widgets, from the theme's tokens. */
export const getSolarChartColors = (theme: Theme) => ({
  forecast: theme.tokens.colors.border,
  actual: theme.tokens.colors.chart1,
  exported: theme.tokens.colors.chart2,
});

/** Poor, fair and good bands as ratios of the forecast. */
export const getSolarPerformanceBands = (theme: Theme): PerformanceBand[] => {
  const { poor, good, ceiling } = SOLAR_PERFORMANCE_THRESHOLDS;
  const percent = (ratio: number) => Math.round(ratio * 100);

  return [
    {
      name: `0-${percent(poor) - 1}% of forecast`,
      from: 0,
      to: poor,
      color: theme.tokens.colors.destructive,
    },
    {
      name: `${percent(poor)}-${percent(good) - 1}% of forecast`,
      from: poor,
      to: good,
      color: theme.tokens.colors.warning,
    },
    {
      name: `${percent(good)}-${percent(ceiling)}% of forecast`,
      from: good,
      to: ceiling,
      color: theme.tokens.colors.success,
    },
  ];
};

/** Actual over reference, or null when the reference is not positive. */
export const performanceRatio = (
  actual: number,
  reference: number
): number | null => (reference > 0 ? actual / reference : null);

/** A ratio as a whole or fixed-decimal percentage, e.g. 0.953 to '95%'. */
export const formatPercent = (ratio: number, decimals = 0) =>
  `${(ratio * 100).toFixed(decimals)}%`;

/** A `YYYY-MM` month key as a short label, e.g. '2026-09' to 'Sep 26'. */
export const monthKeyLabel = (key: string) => {
  const [year, month] = key.split('-');
  return `${MONTH_LABELS[Number(month) - 1]} ${year.slice(2)}`;
};
